/**
 * AdminOnlySidebar — sidebar específico para usuarios ADMIN generales.
 *
 * Sprint 13 — Opción B del análisis /admin-dashboard:
 * Reemplaza AdminSidebar (diseñado para afiliado/asociado) cuando el usuario
 * es ADMIN. Agrupa acciones reales del admin: aprobaciones, moderación,
 * gestión de miembros, padrón oficial, MLS, herramientas destructivas.
 *
 * Uso en admin-dashboard.tsx:
 *   <AdminOnlySidebar show={show} setShow={setShow} />
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';

interface Props {
  show: boolean;
  setShow: (v: boolean) => void;
}

interface SectionItem {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  forComite?: boolean; // visible tambien para admin_comite
}

interface Section {
  title: string;
  icon: string;
  items: SectionItem[];
  defaultOpen?: boolean;
  forComite?: boolean; // toda la seccion visible para admin_comite
}

const SECTIONS: Section[] = [
  {
    title: 'Panel',
    icon: 'fa-gauge',
    defaultOpen: true,
    forComite: true,
    items: [
      { to: '/admin-dashboard', icon: 'fa-gauge', label: 'Dashboard admin' },
      { to: '/admin-comite/dashboard', icon: 'fa-users-gear', label: 'Panel de comité', forComite: true },
    ],
  },
  {
    title: 'Moderación',
    icon: 'fa-circle-check',
    defaultOpen: true,
    items: [
      { to: '/aprobar-resena', icon: 'fa-star-half-stroke', label: 'Aprobar reseñas' },
    ],
  },
  {
    title: 'Gestión de miembros',
    icon: 'fa-users',
    items: [
      { to: '/admin-asociado', icon: 'fa-user-tie', label: 'Asociados' },
      { to: '/admin-afiliado', icon: 'fa-user', label: 'Afiliados' },
      { to: '/admin-inm', icon: 'fa-building', label: 'Inmobiliarias' },
      { to: '/admin-padron-oficial', icon: 'fa-clipboard-check', label: 'Padrón oficial' },
      { to: '/admin-lista-oficial', icon: 'fa-list-check', label: 'Lista oficial' },
    ],
  },
  {
    title: 'Propiedades',
    icon: 'fa-building-circle-check',
    items: [
      { to: '/admin-property', icon: 'fa-house', label: 'Todas las propiedades' },
      { to: '/admin-asignaciones', icon: 'fa-link', label: 'Asignaciones' },
      { to: '/admin-mls-sync', icon: 'fa-rotate', label: 'MLS Sync' },
    ],
  },
  {
    title: 'Capacitación',
    icon: 'fa-graduation-cap',
    defaultOpen: true,
    forComite: true,
    items: [
      { to: '/admin-talleres', icon: 'fa-chalkboard-user', label: 'Talleres', forComite: true },
      { to: '/admin-create-taller', icon: 'fa-plus-circle', label: 'Crear taller', forComite: true },
      { to: '/escanear-asistencia', icon: 'fa-qrcode', label: 'Escanear asistencia', forComite: true },
    ],
  },
];

export default function AdminOnlySidebar({ show, setShow }: Props) {
  const { user } = useUser();
  const location = useLocation();
  const current = location.pathname;

  const esSoloComite = user?.rol === 'ADMIN_COMITE';

  // Si es admin_comite puro, filtrar secciones e items a los marcados forComite
  const seccionesVisibles: Section[] = esSoloComite
    ? SECTIONS
        .filter(s => s.forComite)
        .map(s => ({ ...s, items: s.items.filter(it => it.forComite) }))
        .filter(s => s.items.length > 0)
    : SECTIONS;

  // Persistir estado de secciones abiertas en localStorage
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('ampi_admin_sidebar_sections');
      if (stored) return JSON.parse(stored);
    } catch {}
    // Default: abrir las marcadas con defaultOpen
    return SECTIONS.reduce((acc, s) => {
      acc[s.title] = !!s.defaultOpen;
      return acc;
    }, {} as Record<string, boolean>);
  });

  const toggleSection = (title: string) => {
    setOpenSections(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try {
        localStorage.setItem('ampi_admin_sidebar_sections', JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const userPhoto = (user as any)?.foto || user?.originalDoc?.foto;

  return (
    <div
      className={`simple-sidebar sm-sidebar ${show ? 'd-block d-md-block' : 'd-none d-md-block'}`}
      id="filter_search"
      aria-label="Menú de administrador"
    >
      <div className="search-sidebar_header">
        <h4 className="ssh_heading">{esSoloComite ? 'Menú comité' : 'Menú admin'}</h4>
        <button
          className="w3-bar-item w3-button w3-large"
          onClick={() => setShow(!show)}
          aria-label="Cerrar menú lateral"
          type="button"
        >
          <i className="fa-regular fa-circle-xmark fs-5 text-muted-2" aria-hidden="true"></i>
        </button>
      </div>

      <div className="sidebar-widgets">
        <div className="dashboard-navbar">
          {/* Header de usuario */}
          <div className="d-user-avater text-center">
            {userPhoto ? (
              <img
                src={userPhoto}
                className="img-fluid avater"
                alt={user?.nombre || 'Administrador'}
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '2px solid #0c2339',
                }}
              />
            ) : (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#0c2339',
                  color: '#fff',
                }}
              >
                <i className="fa-solid fa-user-shield fa-2x" aria-hidden="true"></i>
              </div>
            )}
            <h4 className="mt-2 mb-1">{user?.nombre || 'Administrador'}</h4>
            <span className="badge bg-dark">
              <i className={`fa-solid ${esSoloComite ? 'fa-users-gear' : 'fa-shield-halved'} me-1`} aria-hidden="true"></i>
              {esSoloComite ? 'Admin de Comité' : 'Administrador'}
            </span>
            {user?.correo && (
              <div className="small text-muted mt-1 text-truncate" title={user.correo}>
                {user.correo}
              </div>
            )}
          </div>

          <hr />

          {/* Navegación agrupada por secciones */}
          <nav className="d-navigation" aria-label="Secciones de administración">
            {seccionesVisibles.map(section => {
              const isOpen = openSections[section.title] ?? false;
              const hasActive = section.items.some(it => current === it.to);
              return (
                <div key={section.title} className="mb-2">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    aria-expanded={isOpen}
                    className="btn btn-link w-100 d-flex justify-content-between align-items-center text-start px-2 py-2"
                    style={{
                      textDecoration: 'none',
                      color: hasActive ? '#0c2339' : '#495057',
                      fontWeight: hasActive ? 600 : 500,
                      backgroundColor: hasActive ? '#e9ecef' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    <span>
                      <i className={`fa-solid ${section.icon} me-2`} aria-hidden="true"></i>
                      {section.title}
                    </span>
                    <i
                      className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} small`}
                      aria-hidden="true"
                    ></i>
                  </button>

                  {isOpen && (
                    <ul className="list-unstyled ps-3 mt-1" role="menu">
                      {section.items.map(item => {
                        const active = current === item.to;
                        return (
                          <li key={item.to} className={active ? 'active' : ''} role="none">
                            <Link
                              to={item.to}
                              role="menuitem"
                              className="d-flex align-items-center px-2 py-2"
                              style={{
                                textDecoration: 'none',
                                color: active ? '#0c2339' : '#6c757d',
                                fontWeight: active ? 600 : 400,
                                borderRadius: '4px',
                                borderLeft: active ? '3px solid #0c2339' : '3px solid transparent',
                                fontSize: '0.9rem',
                              }}
                            >
                              <i className={`fa-solid ${item.icon} me-2`} style={{ width: 16 }} aria-hidden="true"></i>
                              <span>{item.label}</span>
                              {item.badge && (
                                <span className="badge bg-danger ms-auto">{item.badge}</span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>

          <hr />

          {/* Atajos rápidos */}
          <Link
            to="/ayuda"
            className="d-flex align-items-center px-3 py-2 mt-2"
            style={{
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              color: '#0c2339',
              textDecoration: 'none',
            }}
          >
            <i className="fa-solid fa-circle-question me-2 text-primary" aria-hidden="true"></i>
            <span>Centro de ayuda</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
